import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import { marked } from 'marked';
import React from 'react';
import { ReactComponent as JiraIcon } from '../../../assets/icons/jira-action-icon.svg';
import { ReactComponent as MondayIcon } from '../../../assets/icons/monday-action-icon.svg';
import { ReactComponent as SlackIcon } from '../../../assets/icons/slack-action-icon.svg';
import { DiGit } from 'react-icons/di';
import { generateCategoryIcon } from 'common-icons';
import { IconType } from 'common-types';
import { SeverityType, TicketProvider } from 'common-types';
import { isDevelopment } from 'env-utils';
import {
  FilterType,
  FilterTypeSearch,
  FilterTypeToFilter,
} from '../../../common/filters-utils/filters-utils';
import { resolveRepoIcon } from 'common-icons';
import {
  FixType,
  Issue,
  IssueDetails,
  SeverityBreakdownItem,
  SeverityByCategory,
  SeverityChangeFactorTypes,
  SeverityTrend,
} from '../types/issues-types';
import { RepoTypes } from 'common-types';

const renderer = new marked.Renderer();
renderer.link = function (href, title, text) {
  var link = marked.Renderer.prototype.link.call(this, href, title, text);
  return link.replace('<a', "<a target='_blank' ");
};

marked.setOptions({
  renderer: renderer,
});

export const generateIcons = (
  category?: Issue['category'] | CategoryDisplayName,
  repoType?: RepoTypes,
  fakeApp?: boolean,
) => {
  const CategoryIcon = category && resolveIssueCategoryIcon(category);
  const RepoIcon = repoType && resolveRepoIcon(repoType, fakeApp);
  return { CategoryIcon, RepoIcon };
};

export const resolveIssueCategoryIcon = (
  category: Issue['category'] | CategoryDisplayName,
) => {
  const categoryIcon = generateCategoryIcon(category) as
    | React.ElementType
    | undefined;
  return categoryIcon;
};

export const resolveActionIcon = (actionType: TicketProvider | FixType) => {
  return ActionsMap.get(actionType) || null;
};

export const ActionsMap = new Map<TicketProvider | FixType, IconType>([
  [TicketProvider.Jira, JiraIcon],
  [TicketProvider.Modnay, MondayIcon],
  [TicketProvider.Slack, SlackIcon],
  [FixType.PullRequest, DiGit],
]);

export const issuesFilters = [
  ...(isDevelopment()
    ? [
        {
          type: FilterTypeToFilter.IssueStatus,
          header: FilterType.issueStatus,
        },
      ]
    : []),
  {
    type: FilterTypeToFilter.Apps,
    header: FilterType.apps,
    searchType: FilterTypeSearch.apps,
  },
  {
    type: FilterTypeToFilter.Criticality,
    header: FilterType.criticality,
  },

  {
    type: FilterTypeToFilter.OriginalSeverity,
    header: FilterType.originalSeverity,
  },
  {
    type: FilterTypeToFilter.SeverityChange,
    header: FilterType.severityChange,
  },
  {
    type: FilterTypeToFilter.SeverityChangeReasons,
    header: FilterType.severityChangeReasons,
    searchType: FilterTypeSearch.severityChangeReasons,
  },
  {
    type: FilterTypeToFilter.Category,
    header: FilterType.category,
  },
  {
    type: FilterTypeToFilter.IssueNames,
    header: FilterType.issueNames,
    searchType: isDevelopment()
      ? FilterTypeSearch.issueName
      : FilterTypeSearch.issueNames,
  },
  {
    type: FilterTypeToFilter.Policy,
    header: FilterType.policies,
    searchType: FilterTypeSearch.policies,
  },
  {
    type: FilterTypeToFilter.IssueOwner,
    header: FilterType.issueOwners,
    searchType: isDevelopment()
      ? FilterTypeSearch.issueOwners
      : FilterTypeSearch.issueOwner,
  },
  {
    type: FilterTypeToFilter.SourceTool,
    header: FilterType.sourceTools,
  },
  {
    type: FilterTypeToFilter.CWE,
    header: FilterType.cwe,
    searchType: FilterTypeSearch.cwe,
  },
  {
    type: FilterTypeToFilter.IssueActions,
    header: FilterType.issuesActions,
  },
  ...(isDevelopment()
    ? [
        {
          type: FilterTypeToFilter.UniqueLibs,
          header: FilterType.UniqueLibs,
          searchType: FilterTypeSearch.UniqueLibs,
        },
        {
          type: FilterTypeToFilter.FilePaths,
          header: FilterType.FilePaths,
          searchType: FilterTypeSearch.FilePaths,
        },
      ]
    : []),
  {
    type: FilterTypeToFilter.BusinessPriority,
    header: FilterType.appBusinessPriority,
  },
];

export const pipelineIssuesFilters = [
  {
    type: FilterTypeToFilter.Apps,
    header: FilterType.apps,
    searchType: FilterTypeSearch.apps,
  },
  {
    type: FilterTypeToFilter.Criticality,
    header: FilterType.criticality,
  },

  {
    type: FilterTypeToFilter.Category,
    header: FilterType.category,
  },
  {
    type: FilterTypeToFilter.IssueNames,
    header: FilterType.issueNames,
    searchType: FilterTypeSearch.issueNames,
  },
  {
    type: FilterTypeToFilter.Policy,
    header: FilterType.policies,
    searchType: FilterTypeSearch.policies,
  },
  {
    type: FilterTypeToFilter.JobTriggeredBy,
    header: FilterType.jobTriggeredBy,
    searchType: FilterTypeSearch.jobTriggeredBy,
  },
  {
    type: FilterTypeToFilter.Enforcement,
    header: FilterType.enforcement,
  },
  {
    type: FilterTypeToFilter.CicdIssueStatus,
    header: FilterType.cicdIssueStatus,
  },
  {
    type: FilterTypeToFilter.JobNumber,
    header: FilterType.jobNumber,
    searchType: FilterTypeSearch.jobNumber,
  },
  {
    type: FilterTypeToFilter.PullRequests,
    header: FilterType.pullRequests,
    searchType: FilterTypeSearch.pullRequests,
  },
];

const createSeverityBreakdown = (
  issueDetails: IssueDetails,
): SeverityBreakdownItem[] => {
  const { severityChangedReason } = issueDetails;
  const sorted = [...severityChangedReason].sort(
    (a, b) => b.changeNumber - a.changeNumber,
  );

  return sorted.map(raw => ({
    name: raw.shortName,
    trend: resolveTrend(raw.changeNumber),
    // can't be zero - we want to show an item there
    itemsCount: Math.ceil(raw.changeNumber) || 1,
    description: raw.reason,
    changeNumber: raw.changeNumber,
    changeCategory: raw.changeCategory !== null ? raw.changeCategory : '',
  }));
};

export const createIssueDetails = (
  issueDetails: IssueDetails,
): IssueDetails => {
  return {
    ...issueDetails,
    severity: issueDetails.severity.toLowerCase() as SeverityType,
    originalToolSeverity:
      issueDetails.originalToolSeverity.toLowerCase() as SeverityType,
    severityBreakdown: createSeverityBreakdown(issueDetails),
    secondTitleMarkup: marked.parse(issueDetails.secondTitle || ''),
    recommendationMarkup: marked.parse(issueDetails.recommendation || ''),
  };
};

const resolveTrend = (changeNumber: number): SeverityTrend => {
  if (changeNumber === 0) {
    return SeverityTrend.Neutral;
  } else if (changeNumber > 0) {
    return SeverityTrend.Up;
  } else {
    return SeverityTrend.Down;
  }
};

export const mappingSeveritisChangeReason = (
  severities: SeverityBreakdownItem[],
) => {
  const keys = [
    SeverityChangeFactorTypes.Reachability,
    SeverityChangeFactorTypes.Exploitability,
    SeverityChangeFactorTypes.Damage,
  ];
  const obj = severities.reduce((acc, severity) => {
    if (acc[severity.changeCategory]) {
      acc[severity.changeCategory].push(severity);
    } else {
      acc[severity.changeCategory] = [severity];
    }
    return acc;
  }, {});

  keys.forEach(key => {
    if (!Object.keys(obj).includes(key)) {
      obj[key] = [];
    }
  });
  const sortedObj = keys.reduce((acc, key) => {
    if (obj.hasOwnProperty(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
  Object.entries(sortedObj).forEach(sev => {
    if (Array.isArray(sev[1])) {
      if (sev[1].length === 0) {
        sev[1].push({
          changeNumber: 0,
          name: 'None Found',
          trend: 'neutral',
        });
      }
    }
  });
  return sortedObj as SeverityByCategory;
};
